/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, uniq, isEqual, debounce, take, drop } from 'lodash';
import {
  ButtonIcon,
  Dropdown,
  Icon,
  Pagination,
  Typography,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { useGetPlayerScout } from './useGetPlayerScout';
import { API_STAT_UNITS } from '../../../../utils/constants/api';
import { useGetPlayerStatsCategoryDefinitions } from '../../../../utils/hooks/useGetPlayerStatCategories';
import {
  getCategories,
  formatScoutSortableTableRows,
  sortPlayers,
  extractPlayerAndTeamId,
} from './ScoutPlayers.dataManipulation';
import {
  PLAYS_STAT_KEY,
  SCOUT_PAGINATION_OPTIONS,
  SCOUT_PLAYERS_STICKY_COLUMNS,
} from './ScoutPlayers.constants';
import {
  ScoutPlayersWrapper,
  ScoutPlayersStatusWrapper,
  ScoutPlayersPagination,
  TableWrapper,
} from './ScoutPlayers.styles';
import StatCategoryTabs from './StatCategoryTabs/StatCategoryTabs';
import Dimmer from '../../../../components/Dimmer/Dimmer';
import SortableTable from '../../../../components/Tables/SortableTable/SortableTable';
import { mf_Leagues, mf_Seasons } from '../../../../apollo';
import LoaderLabelled from '../../../../components/LoaderLabelled/LoaderLabelled';
import {
  TableHeadWrapper,
  TableStarWrapper,
} from '../../../../components/Tables/SortableTable/SortableTable.styles';
import { SCOUT_TEMPLATE_MODES } from '../../Scout.constants';
import useScoutTableHeaders from './useScoutTableHeaders';
import { trackContentFavoriteEvent } from '../../../../utils/helpers/snowplow';
import ExternalLink from '../../../../components/ExternalLink/ExternalLink';
import { ErrorPageContent } from '../../../../components/ErrorPage/ErrorPage.styles';
import { DEFAULT_SCOUT_POSITION } from '../ScoutResults.constants';
import LinkButton from '../../../../components/buttons/LinkButton/LinkButton';
import { getRosterPositionByCode } from '../../../../utils/helpers/positions';
import {
  USER_GROUP,
  USER_ROLES,
  VIDEO_JWT_CLAIMS,
} from '../../../../utils/constants/auth';
import { useStore } from '../../../../store/useStore';
import VideoModal from '../../../../components/Video/VideoModal';
import ScoutPlayersHeader from './ScoutPlayersHeader/ScoutPlayersHeader';

const ScoutPlayers = () => {
  // data
  const selectedStats = useStore((s) => s.selectedStats);
  const rosterPositions = useStore((s) => s.selectedPositions);
  const showFavourites = useStore((s) => s.showFavourites);
  const selectedBiometrics = useStore((s) => s.selectedBiometrics);
  const statDefinitions = useStore((s) => s.statDefinitions);
  const playersCount = useStore((s) => s.scoutPlayersCount);
  const setPlayersCount = useStore((s) => s.setScoutPlayersCount);
  const [tableData, setTableData] = useState([]);
  // favourites
  const scoutFavourites = useStore((s) => s.scoutFavourites);
  const setScoutFavourites = useStore((s) => s.setScoutFavourites);
  const persistScoutFavourites = useStore((s) => s.persistScoutFavourites);
  // toast
  const setToast = useStore((s) => s.setToast);
  // video
  const setVideoModalOpen = useStore((s) => s.setVideoModalOpen);
  // table state
  const searchValue = useStore((s) => s.scoutTableSearchText);
  const pageSize = useStore((s) => s.scoutPlayersPageSize);
  const setPageSize = useStore((s) => s.setScoutPlayersPageSize);
  const activePage = useStore((s) => s.scoutPlayersActivePage);
  const setActivePage = useStore((s) => s.setScoutPlayersActivePage);
  const sortDirection = useStore((s) => s.scoutPlayersSortDirection);
  const sortKey = useStore((s) => s.scoutPlayersSortKey);
  const handleSort = useStore((s) => s.handleScoutPlayersSort);
  // table tab state
  const selectedCategoryName = useStore((s) => s.scoutSelectedCategoryName);
  const selectedSubCategoryName = useStore(
    (s) => s.scoutSelectedSubCategoryName
  );
  const { categoryDefinitions: apiStatCategories } =
    useGetPlayerStatsCategoryDefinitions();
  const statCategories = getCategories(apiStatCategories);
  const setScoutStatCategories = useStore((s) => s.setScoutStatCategories);
  setScoutStatCategories(statCategories);

  const selectedStatCategory = statCategories.find(
    (s) => s.name === selectedCategoryName
  );
  const selectedStatSubCategory = selectedStatCategory?.subcategories.find(
    (s) => s.name === selectedSubCategoryName
  );
  const filterStatKeys = isEmpty(selectedStats)
    ? []
    : selectedStats?.map((s) => s.key);
  const columnStatKeys = selectedStatSubCategory?.stats;
  const queryStatKeys = uniq(
    (Array.isArray(columnStatKeys) ? columnStatKeys : [])
      .concat(PLAYS_STAT_KEY)
      .concat(filterStatKeys)
  );
  const bioStatKeys = isEmpty(selectedBiometrics)
    ? []
    : selectedBiometrics?.map((s) => s.key);

  const selectedBiometricsIncludingName = [
    ...selectedBiometrics,
    { key: 'playerName', value: searchValue },
  ];

  // Query: GetPlayerScout
  const {
    players,
    isLoading,
    error: hasError,
  } = useGetPlayerScout(
    selectedStats,
    rosterPositions,
    queryStatKeys,
    selectedBiometricsIncludingName,
    scoutFavourites
  );

  // if filters change, then results list changes ~ revert to page 1
  useEffect(() => {
    setActivePage(1);
  }, [selectedStats, rosterPositions, selectedBiometrics]);

  const { headers } = useScoutTableHeaders({
    statDefinitions,
    columnStatKeys,
    filterStatKeys,
    bioStatKeys,
    selectedCategoryName,
  });

  const stringHeaderIndexes = headers
    .map((h, i) => (h.units === API_STAT_UNITS.STRING ? i : null))
    .filter((m) => m !== null);
  const alignmentFunction = (columnIndex) =>
    stringHeaderIndexes.includes(columnIndex) ? 'left' : 'right';

  // Sortable headers have an exacting prop type
  const sortableTableHeaders = headers?.map((header) => {
    const formattedHeader = {
      id: header.key,
      key: header.key,
      label: header.label,
      isSortable: true,
      description: header.description,
      width: header.width,
      hasVideo: header.hasVideo,
    };
    return formattedHeader;
  });

  const playersSkipped = (activePage - 1) * pageSize;
  const playersAreFavourites = players.filter(
    (p) => Array.isArray(scoutFavourites) && scoutFavourites.includes(p.id)
  );

  useEffect(() => {
    if (!isLoading && Array.isArray(players)) {
      const newCount = showFavourites
        ? playersAreFavourites?.length
        : players?.length;
      if (playersCount !== newCount) {
        setPlayersCount(newCount);
      }
    }
  }, [isLoading, players, showFavourites, playersAreFavourites, playersCount]);

  const leagueId = useReactiveVar(mf_Leagues) || 1409;
  const seasonId = useReactiveVar(mf_Seasons) || 1000;

  const sortedPlayers = sortPlayers(
    showFavourites ? playersAreFavourites : players,
    sortKey,
    sortDirection
  );
  const visiblePlayers = take(drop(sortedPlayers, playersSkipped), pageSize);

  const newTableData = useMemo(
    () =>
      formatScoutSortableTableRows(
        visiblePlayers,
        headers,
        leagueId,
        seasonId,
        scoutFavourites
      ),
    [visiblePlayers, headers, leagueId, seasonId, scoutFavourites]
  );

  const lastSetTableData = useRef(tableData);
  const prevCategoryName = useRef(selectedCategoryName);
  const prevSubCategoryName = useRef(selectedSubCategoryName);

  useEffect(() => {
    // Check if category/subcategory changed
    const categoryChanged =
      prevCategoryName.current !== selectedCategoryName ||
      prevSubCategoryName.current !== selectedSubCategoryName;

    if (
      !isLoading &&
      Array.isArray(players) &&
      (!isEqual(newTableData, lastSetTableData.current) || categoryChanged)
    ) {
      setTableData(newTableData);
      lastSetTableData.current = newTableData;
    }

    // Update refs
    prevCategoryName.current = selectedCategoryName;
    prevSubCategoryName.current = selectedSubCategoryName;
  }, [
    newTableData,
    isLoading,
    players,
    selectedCategoryName,
    selectedSubCategoryName,
  ]);

  const lastSaved = useRef(scoutFavourites);
  const handleMenuAction = (action, data) => {
    if (action === SCOUT_TEMPLATE_MODES.FAVOURITE) {
      const { playerId } = data.favourite;
      const safePrev = Array.isArray(scoutFavourites) ? scoutFavourites : [];
      const newFavourites = safePrev.includes(playerId)
        ? safePrev.filter((id) => id !== playerId)
        : [...safePrev, playerId];
      const isRemoving = safePrev.includes(playerId);
      trackContentFavoriteEvent(
        isRemoving ? 'Remove' : 'Add',
        `${playerId}`,
        'Scout Players'
      );
      setScoutFavourites(newFavourites);
    }
  };

  // Debounce only the API call, not the UI update
  const debouncedPersist = useRef(
    debounce(async () => {
      await persistScoutFavourites();
      setToast(
        {
          title: '',
          message: 'Favorites updated successfully',
          type: 'success',
        },
        true
      );
      lastSaved.current = scoutFavourites;
    }, 500)
  ).current;

  useEffect(() => {
    if (!isEqual(scoutFavourites, lastSaved.current)) {
      debouncedPersist();
    }
  }, [scoutFavourites, debouncedPersist]);

  // video authorisation
  const { user } = useKitbagAuth();
  const isAuthorised =
    user['https://statsbomb.com/accountID'] === USER_GROUP.STATSBOMB ||
    user[VIDEO_JWT_CLAIMS]?.roles.includes(USER_ROLES.VIDEO_CUSTOMER);

  return (
    <>
      <ScoutPlayersWrapper>
        <ScoutPlayersHeader players={players} headers={headers} />
        <StatCategoryTabs />
        <TableWrapper>
          {isLoading && (
            <Dimmer
              active
              style={{ minHeight: 'calc(100vh - 316px)', maxWidth: '100%' }}
            >
              <LoaderLabelled text="Loading Data" />
            </Dimmer>
          )}
          {!hasError && (
            <TableHeadWrapper>
              <SortableTable
                name="scout-table"
                headers={sortableTableHeaders}
                rows={tableData}
                stickyColumns={SCOUT_PLAYERS_STICKY_COLUMNS}
                alignment={alignmentFunction}
                handleSort={(statName) => handleSort(statName, headers)}
                sortDirection={sortDirection}
                sortBy={sortKey}
                border="0"
                rowHeight="2rem"
                isHeadSticky
                nonTableHeight="calc(100vh - 16.4rem)"
                onMenuAction={handleMenuAction}
                renderClickableStatCell={(cell, rowIndex, rows) =>
                  isAuthorised ? (
                    <LinkButton
                      onClickFn={() => {
                        // pulling playerId and teamId from the link
                        // e.g. /players/overview/12345/32423423/67890
                        const { playerId, teamId } = extractPlayerAndTeamId(
                          rows[rowIndex].name.link
                        );
                        setVideoModalOpen({
                          videoModalOpen: true,
                          selectedStatName: cell.id,
                          selectedPlayerId: playerId,
                          selectedTeamId: teamId,
                          selectedGameId: '',
                          selectedTitle: `${rows[rowIndex].name.label}, ${
                            statDefinitions.find((s) => s.name === cell.id)
                              .prettyName
                          }`,
                          selectedPosition: getRosterPositionByCode(
                            rows[rowIndex].rosterPositionCode.label
                          ).apiCode,
                        });
                      }}
                      tooltip="Watch video"
                      label={cell.label}
                      testId={`link-button-${cell.id}-${rowIndex}`}
                    />
                  ) : (
                    cell.label
                  )
                }
                renderFavouriteCell={(cell, rowIndex, rows) => (
                  <TableStarWrapper $filled={cell?.checked}>
                    <ButtonIcon
                      icon={cell?.checked ? 'StarFilled' : 'StarUnfilled'}
                      variant="ghost"
                      size="small"
                      title={
                        cell?.checked
                          ? 'Remove from Favorites'
                          : 'Add to Favorites'
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMenuAction(
                          SCOUT_TEMPLATE_MODES.FAVOURITE,
                          rows[rowIndex]
                        );
                      }}
                    />
                  </TableStarWrapper>
                )}
              />
              {playersCount === 0 && !isLoading && (
                <ScoutPlayersStatusWrapper>
                  <Icon variant="Info" size="regular" />
                  <Typography variant="headingLarge">No Results</Typography>
                  <Typography
                    variant="bodyRegular"
                    data-testid="no-results-text"
                  >
                    Try adjusting your filters or starting over
                  </Typography>
                </ScoutPlayersStatusWrapper>
              )}
            </TableHeadWrapper>
          )}
          {hasError && !isLoading && (
            <ScoutPlayersStatusWrapper>
              <ErrorPageContent>
                <Icon variant="Warning" size="regular" />
                <Typography variant="headingLarge">
                  Unable to Load Content
                </Typography>
                <Typography variant="bodyRegular">
                  Try refreshing the page. If the problem persists,{' '}
                  <ExternalLink href={process.env.SUPPORT_URL}>
                    contact support
                  </ExternalLink>
                  .
                </Typography>
              </ErrorPageContent>
            </ScoutPlayersStatusWrapper>
          )}
        </TableWrapper>
        <ScoutPlayersPagination>
          <Pagination
            activePage={activePage}
            onChange={({ pageSelected }) => setActivePage(pageSelected)}
            itemsPerPage={pageSize}
            dataLength={playersCount}
            hasEdgePageButtons
            labelText={`of ${playersCount} player${
              playersCount !== 1 ? 's' : ''
            }`}
            size="small"
          />
          <Dropdown
            id="scout-players-pagination-options"
            isSearchable={false}
            menuAlignment="right"
            menuPlacement="top"
            menuPosition="absolute"
            onChange={({ value }) => {
              setPageSize(value);
              setActivePage(1);
            }}
            options={SCOUT_PAGINATION_OPTIONS}
            value={SCOUT_PAGINATION_OPTIONS.find(
              (option) => option.value === pageSize
            )}
            labelPosition="none"
            variant="tertiary"
            size="small"
          />
          per page
        </ScoutPlayersPagination>
        <VideoModal
          onClose={() => {
            setVideoModalOpen({
              videoModalOpen: false,
              selectedStatName: '',
              selectedPlayerId: '',
              selectedTeamId: '',
              selectedGameId: '',
              selectedTitle: '',
              selectedPosition: DEFAULT_SCOUT_POSITION,
            });
          }}
        />
      </ScoutPlayersWrapper>
    </>
  );
};

export default ScoutPlayers;
